import {
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    MessageBar,
    MessageBarType,
    PrimaryButton,
} from "@fluentui/react";
import {FC, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {AuthContext} from "../contexts/AuthContext";
import {
    OnixPhoneUserTaskEnum,
    OnixPhoneUserTasksQuery,
    useMarkOnixPhoneUserTaskQueueMutation,
    useOnixPhoneUserTasksQuery,
} from "../graphql-types";

type OnixPhoneTask =
    NonNullable<NonNullable<OnixPhoneUserTasksQuery["onixPhoneUserTaskQueue"]>[number]>;

export const OnixPhoneUserTaskListener: FC = () => {
    const {UserInfo} = useContext(AuthContext);
    const [pendingTasks, setPendingTasks] = useState<OnixPhoneTask[]>([]);
    const [errorMessage, setErrorMessage] = useState<string>();

    const {data, refetch} = useOnixPhoneUserTasksQuery({
        fetchPolicy: "network-only",
        notifyOnNetworkStatusChange: true,
        skip: !UserInfo,
    });

    const [markTask, {loading: completing}] = useMarkOnixPhoneUserTaskQueueMutation();

    useEffect(() => {
        if (!UserInfo) {
            setPendingTasks([]);
            return;
        }
        refetch();

        const handleFocus = () => refetch();
        const handleVisibility = () => {
            if (document.visibilityState === "visible") {
                refetch();
            }
        };

        window.addEventListener("focus", handleFocus);
        document.addEventListener("visibilitychange", handleVisibility);

        return () => {
            window.removeEventListener("focus", handleFocus);
            document.removeEventListener("visibilitychange", handleVisibility);
        };
    }, [UserInfo, refetch]);

    useEffect(() => {
        if (!data) return;

        const showableTasks = (data.onixPhoneUserTaskQueue ?? []).filter(
            (task): task is OnixPhoneTask =>
                !!task &&
                !task.isCompleted &&
                task.task === OnixPhoneUserTaskEnum.ShowMessageToUser,
        );

        setPendingTasks((current) => {
            const incomingIds = new Set(showableTasks.map((task) => task.id));
            const preserved = current.filter((task) => incomingIds.has(task.id));
            const preservedIds = new Set(preserved.map((task) => task.id));
            const newOnes = showableTasks.filter((task) => !preservedIds.has(task.id));
            return [...preserved, ...newOnes];
        });
    }, [data]);

    const activeTask = useMemo(() => pendingTasks[0], [pendingTasks]);

    const finishActiveTask = useCallback(
        async (failed: boolean = false) => {
            if (!activeTask || completing) return;
            setErrorMessage(undefined);
            try {
                await markTask({
                    variables: {
                        taskId: activeTask.id,
                        failed,
                        response: failed
                            ? "Task could not be completed on web"
                            : "Task shown in web app",
                    },
                });
                setPendingTasks((current) =>
                    current.filter((task) => task.id !== activeTask.id),
                );
                await refetch();
            } catch (e) {
                const message = e instanceof Error ? e.message : "Failed to mark task.";
                setErrorMessage(message);
            }
        },
        [activeTask, completing, markTask, refetch],
    );

    if (!activeTask) {
        return null;
    }

    return (
        <Dialog
            hidden={!activeTask}
            dialogContentProps={{
                type: DialogType.normal,
                title: "Onix obavijest",
            }}
            modalProps={{isBlocking: true}}
            minWidth={360}
            onDismiss={() => finishActiveTask(false)}
        >
            {errorMessage ? (
                <MessageBar messageBarType={MessageBarType.error} styles={{root: {marginBottom: 8}}}>
                    {errorMessage}
                </MessageBar>
            ) : null}
            <div style={{whiteSpace: "pre-wrap", marginTop: 8}}>
                {activeTask.taskData || "Nema dodatnih detalja za ovaj zadatak."}
            </div>
            <DialogFooter>
                <PrimaryButton
                    text="U redu"
                    onClick={() => finishActiveTask(false)}
                    disabled={completing}
                />
                <DefaultButton
                    text="Zatvori"
                    onClick={() => finishActiveTask(false)}
                    disabled={completing}
                />
            </DialogFooter>
        </Dialog>
    );
};
